/**
 * Controls — from the Dashboard (segmented control, pager, info dot), Session Setup (chips, mood tiles, fields,
 * range, checklist rows, buttons) and AI Briefing (search field, removable chips). Literals verbatim; see `kit.ts`.
 */
import type { ChangeEvent, CSSProperties, ReactNode, SyntheticEvent } from 'react';
import { css, s } from './style';

export type Handler = (event?: SyntheticEvent) => void;
export type Primitive = string | number | boolean | null | undefined;

/* ── Segmented control — the app's primary selection signal (dashboard 259, wrapper ×4) ─────────────────────── */
export const SEGMENTED_BASE =
  'display:flex;align-items:center;gap:2px;padding:3px;border-radius:99px;border:1px solid var(--l8)';
export function Segmented({ children, extra }: { children?: ReactNode; extra?: string }) {
  // the dashboard's wrapper carries `flex:none` after the border
  return <div style={css(SEGMENTED_BASE + (extra ? ';' + extra : ';flex:none'))}>{children}</div>;
}
/** The reference writes the option in five declaration orders; each variant reproduces one byte for byte. */
export type SegmentedVariant = 'a' | 'b' | 'c' | 'd' | 'e';
const SEGMENTED_TAIL: Record<SegmentedVariant, (fw: string) => string> = {
  a: (fw) => `font-size:10.5px;font-weight:${fw};cursor:pointer;white-space:nowrap`, // dashboard 259 (×3)
  b: (fw) => `font-weight:${fw};font-size:10.5px;cursor:pointer`,
  c: (fw) => `font-weight:${fw};font-size:10.5px;cursor:pointer;white-space:nowrap`,
  d: () => 'font-size:10.5px;font-weight:500;cursor:pointer;font-variant-numeric:tabular-nums',
  e: () => 'font-size:11.5px;font-weight:500;cursor:pointer',
};
export interface SegmentedOptionProps {
  bg: Primitive;
  fg: Primitive;
  fw?: Primitive;
  variant?: SegmentedVariant;
  onClick?: Handler;
  title?: string;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
  hoverClass?: string;
  children?: ReactNode;
}
export function SegmentedOption({
  bg,
  fg,
  fw = '500',
  variant = 'a',
  onClick,
  hoverClass,
  children,
  ...aria
}: SegmentedOptionProps) {
  const lit = `padding:4px 11px;border-radius:99px;border:none;background:${s(bg)};color:${s(fg)};${SEGMENTED_TAIL[variant](s(fw))}`;
  return (
    <button className={hoverClass} onClick={onClick} style={css(lit)} {...aria}>
      {children}
    </button>
  );
}

/* ── Chips ──────────────────────────────────────────────────────────────────────────────────────────────────── */
/** Selection chip — Session Setup's focus/strategy chips (line 842, ×2 groups). */
export interface ChipProps {
  bd: Primitive;
  bg: Primitive;
  fg: Primitive;
  onClick?: Handler;
  hoverClass?: string;
  title?: string;
  'aria-pressed'?: boolean;
  'aria-label'?: string;
  children?: ReactNode;
}
export function Chip({ bd, bg, fg, onClick, hoverClass, children, ...aria }: ChipProps) {
  const lit = `font-size:11.5px;padding:6px 14px;border-radius:99px;border:1px solid ${s(bd)};background:${s(bg)};color:${s(fg)};cursor:pointer`;
  return (
    <button className={hoverClass} onClick={onClick} style={css(lit)} {...aria}>
      {children}
    </button>
  );
}
/** Status chip — Briefing's impact/sentiment tag (line 743, ×4 tones). */
export function StatusChip({ bg, fg, children }: { bg: string; fg: string; children?: ReactNode }) {
  return (
    <span
      style={css(
        `font-size:9.5px;font-weight:600;letter-spacing:0.06em;padding:2px 8px;border-radius:99px;background:${bg};color:${fg}`,
      )}
    >
      {children}
    </span>
  );
}
/** Outline chip — Briefing's neutral tag (line 744, ×2). */
export function OutlineChip({ children }: { children?: ReactNode }) {
  return (
    <span
      style={css(
        'font-size:9.5px;padding:2px 8px;border-radius:99px;border:1px solid var(--l10);color:var(--muted2)',
      )}
    >
      {children}
    </span>
  );
}
/** Removable chip — Briefing's watchlist symbol (line 741). */
export function RemovableChip({
  children,
  onRemove,
  label,
}: {
  children?: ReactNode;
  onRemove?: Handler;
  label?: string;
}) {
  return (
    <span
      style={css(
        'display:inline-flex;align-items:center;gap:7px;font-size:11.5px;font-weight:500;padding:5px 7px 5px 12px;border-radius:99px;border:1px solid var(--l12);background:var(--surface)',
      )}
    >
      {children}
      <button
        onClick={onRemove}
        aria-label={label}
        style={css(
          'border:none;background:transparent;color:var(--faint);cursor:pointer;padding:0;display:flex',
        )}
      >
        <i className="ph ph-x" style={css('font-size:10px')}></i>
      </button>
    </span>
  );
}

/* ── Buttons ────────────────────────────────────────────────────────────────────────────────────────────────── */
/** Ghost button — Session Setup (line 806, ×2). */
export function GhostButton({
  icon,
  onClick,
  hoverClass,
  children,
  ...aria
}: {
  icon?: string;
  onClick?: Handler;
  hoverClass?: string;
  children?: ReactNode;
  'aria-label'?: string;
}) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      style={css(
        'display:inline-flex;align-items:center;gap:6px;font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l12);background:transparent;color:var(--text-mid);cursor:pointer',
      )}
      {...aria}
    >
      {icon ? <i className={`ph ${icon}`} style={css('font-size:12px')}></i> : null}
      {children}
    </button>
  );
}
/** Accent button — Session Setup's primary action (line 916). */
export function AccentButton({
  icon,
  tone = 'var(--acc-fill)',
  ink = 'var(--acc)',
  onClick,
  children,
  ...aria
}: {
  icon?: string;
  tone?: string;
  ink?: string;
  onClick?: Handler;
  children?: ReactNode;
  'aria-label'?: string;
}) {
  return (
    <button
      onClick={onClick}
      style={css(
        `display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:500;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, ${tone} 55%, transparent);background:color-mix(in srgb, ${tone} 10%, transparent);color:${ink};cursor:pointer`,
      )}
      {...aria}
    >
      {icon ? <i className={`ph ${icon}`} style={css('font-size:12px')}></i> : null}
      {children}
    </button>
  );
}
/** Dashed button — Session Setup's "add" affordance (line 897). */
export function DashedButton({
  onClick,
  children,
  ...aria
}: {
  onClick?: Handler;
  children?: ReactNode;
  'aria-label'?: string;
}) {
  return (
    <button
      onClick={onClick}
      style={css(
        'font-size:11px;padding:6px 13px;border-radius:99px;border:1px dashed var(--l10);background:transparent;color:var(--muted);cursor:pointer',
      )}
      {...aria}
    >
      {children}
    </button>
  );
}
/** Ghost icon button — 26px (risk surfaces) / 24px (composer close). */
export interface IconButtonProps {
  icon: string;
  size?: number;
  iconSize?: number;
  color?: string;
  onClick?: Handler;
  hoverClass?: string;
  title?: string;
  'aria-label'?: string;
  disabled?: boolean;
}
export function IconButton({
  icon,
  size = 26,
  iconSize = 13,
  color = 'var(--faint)',
  onClick,
  hoverClass,
  disabled,
  ...aria
}: IconButtonProps) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      disabled={disabled}
      style={css(
        `width:${size}px;height:${size}px;border:none;background:transparent;color:${color};cursor:pointer;border-radius:6px`,
      )}
      {...aria}
    >
      <i className={`ph ${icon}`} style={css(`font-size:${iconSize}px`)}></i>
    </button>
  );
}
/** Pager caret — Dashboard's calendar month stepper (line 460, ×2). */
export function PagerButton({
  icon,
  color = 'var(--text-mid)',
  cursor = 'pointer',
  onClick,
  title,
  disabled,
  hoverClass,
  ...aria
}: {
  icon: Primitive;
  color?: Primitive;
  cursor?: Primitive;
  onClick?: Handler;
  title?: string;
  disabled?: boolean;
  hoverClass?: string;
  'aria-label'?: string;
}) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={css(
        `width:26px;height:22px;border-radius:99px;border:none;background:transparent;color:${s(color)};cursor:${s(cursor)};display:flex;align-items:center;justify-content:center`,
      )}
      {...aria}
    >
      <i className={`ph ${s(icon)}`} style={css('font-size:12px')}></i>
    </button>
  );
}
/** Info dot — the Dashboard's tooltip affordance (line 442, ×2). */
export function InfoDot({ title }: { title: string }) {
  return (
    <i className="ph ph-info" title={title} style={css('font-size:12px;color:var(--ghost);cursor:help')}></i>
  );
}

/* ── Toggle (risk surfaces; the design language's switch) ───────────────────────────────────────────────────── */
export interface ToggleProps {
  trackBg: Primitive;
  knob: Primitive;
  knobBg: Primitive;
  onClick?: Handler;
  title?: string;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
}
export function Toggle({ trackBg, knob, knobBg, onClick, ...aria }: ToggleProps) {
  return (
    <button
      onClick={onClick}
      style={css(
        `width:34px;height:20px;border-radius:99px;border:none;background:${s(trackBg)};cursor:pointer;position:relative;flex:none;padding:0`,
      )}
      {...aria}
    >
      <span
        style={css(
          `position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:50%;background:${s(knobBg)};transform:${s(knob)};transition:transform 160ms cubic-bezier(0.16,1,0.3,1)`,
        )}
      ></span>
    </button>
  );
}

/* ── Fields — Session Setup (lines 826, 833, 880) and Briefing's search (line 605) ──────────────────────────── */
export const FIELD_BASE =
  'width:100%;margin-top:6px;padding:8px 12px;border-radius:9px;border:1px solid var(--l10);background:var(--fill);color:var(--text);font-size:12px;outline:none';
export interface TextFieldProps {
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  tabular?: boolean;
  roomy?: boolean;
  extra?: string;
  'aria-label'?: string;
  type?: string;
  readOnly?: boolean;
}
export function TextField({
  value,
  onChange,
  placeholder,
  tabular,
  roomy,
  extra,
  type = 'text',
  readOnly,
  ...aria
}: TextFieldProps) {
  const base = roomy
    ? 'width:100%;margin-top:6px;padding:9px 13px;border-radius:9px;border:1px solid var(--l10);background:var(--fill);color:var(--text);font-size:12px;outline:none'
    : FIELD_BASE;
  const lit = base + (tabular ? ';font-variant-numeric:tabular-nums' : '') + (extra ? ';' + extra : '');
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      style={css(lit)}
      {...aria}
    />
  );
}
/** Session Setup's pre-session note (line 891). */
export function TextArea({
  value,
  onChange,
  placeholder,
  ...aria
}: {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  'aria-label'?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={css(
        'width:100%;height:104px;resize:none;margin-top:11px;padding:11px 13px;border-radius:10px;border:1px solid var(--l10);background:var(--fill);color:var(--text);font-size:12px;line-height:1.6;outline:none',
      )}
      {...aria}
    />
  );
}
/** Search field — Briefing's watchlist input (line 605). */
export function SearchField({
  value,
  onChange,
  placeholder,
  ...aria
}: {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  'aria-label'?: string;
}) {
  return (
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={css(
        'width:220px;padding:7px 12px;border-radius:8px;border:1px solid var(--l10);background:var(--fill);color:var(--text);font-size:12px;outline:none',
      )}
      {...aria}
    />
  );
}
/** Range slider — Session Setup's energy control (line 823). The `--fill` custom property drives the track. */
export function RangeSlider({
  value,
  min = 1,
  max = 10,
  fill,
  onChange,
  ...aria
}: {
  value?: number;
  min?: number;
  max?: number;
  fill: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  'aria-label'?: string;
}) {
  return (
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={onChange}
      style={css(`width:100%;margin-top:10px;--fill:${fill}`)}
      {...aria}
    />
  );
}

/* ── Mood tile — Session Setup's mental-state picker (line 819) ─────────────────────────────────────────────── */
export interface MoodTileProps {
  icon: string;
  label: ReactNode;
  bd: Primitive;
  bg: Primitive;
  fg: Primitive;
  onClick?: Handler;
  hoverClass?: string;
  'aria-pressed'?: boolean;
}
export function MoodTile({ icon, label, bd, bg, fg, onClick, hoverClass, ...aria }: MoodTileProps) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      style={css(
        `display:flex;flex-direction:column;align-items:center;gap:5px;padding:11px 0;width:76px;border-radius:12px;border:1px solid ${s(bd)};background:${s(bg)};color:${s(fg)};cursor:pointer`,
      )}
      {...aria}
    >
      <i className={`ph ${s(icon)}`} style={css('font-size:19px')}></i>
      <span style={css('font-size:10.5px;font-weight:500')}>{label}</span>
    </button>
  );
}

/* ── Checklist row — Session Setup's rule/limit rows (lines 860, 874) ───────────────────────────────────────── */
export interface CheckRowProps {
  icon: string;
  iconColor: string;
  label: ReactNode;
  labelColor?: string;
  status?: ReactNode;
  statusColor?: string;
  onClick?: Handler;
  asButton?: boolean;
}
export function CheckRow({
  icon,
  iconColor,
  label,
  labelColor = 'var(--text)',
  status,
  statusColor = 'var(--muted2)',
  onClick,
  asButton,
}: CheckRowProps) {
  const inner = (
    <>
      <i className={`ph ${icon}`} style={css(`font-size:13px;color:${iconColor}`)}></i>
      <span style={css(`font-size:11.5px;flex:1;min-width:0;color:${labelColor}`)}>{label}</span>
      {status === undefined ? null : (
        <span style={css(`font-size:10.5px;color:${statusColor};font-variant-numeric:tabular-nums`)}>
          {status}
        </span>
      )}
    </>
  );
  return asButton ? (
    <button
      onClick={onClick}
      style={css(
        'display:flex;align-items:center;gap:9px;padding:7px 0;border:none;background:transparent;text-align:left;cursor:pointer;border-radius:6px',
      )}
    >
      {inner}
    </button>
  ) : (
    <div style={css('display:flex;align-items:center;gap:9px;padding:7px 0')}>{inner}</div>
  );
}
export type { CSSProperties };
